import { useLocale } from "@hooks/useLocale";
import { useUnits } from "@units/useUnits";
import type { MudVolumeOut } from "@api/types";
import type { MessageKey } from "@i18n/messages";

/**
 * Volumes section (§8) — the day's mud-system inventory + losses as a grid of
 * cells. Losses (surface/downhole) carry a warn tone. Every volume renders
 * through the report's unit profile (bbl for US, m³ for AR) — never a locale
 * guess. Read-only display; the served record is the source of truth.
 */
export function MudVolumes({
  volumes,
  unitProfileKey,
}: {
  volumes: MudVolumeOut;
  unitProfileKey?: string | null;
}) {
  const { t } = useLocale();
  const { convertField, fmt } = useUnits(unitProfileKey);

  const cell = (col: keyof MudVolumeOut, labelKey: MessageKey, warn = false) => {
    const raw = volumes[col] as number | null;
    const cv = convertField("mud_volume", col as string, raw, unitProfileKey);
    return (
      <div className="mud-vol-cell" data-owner="p" key={col as string}>
        <div className="mud-vol-l">{t(labelKey)}</div>
        <div className={`mud-vol-v tab-nums${warn ? " warn" : ""}`}>
          {fmt(cv, { maximumFractionDigits: 0 })}
        </div>
      </div>
    );
  };

  return (
    <section className="card">
      <div className="panel-head-row">
        <h3>{t("mud.section.volumes")}</h3>
      </div>
      <div className="mud-vol-grid">
        {cell("active", "mud.vol.active")}
        {cell("reserve", "mud.vol.reserve")}
        {cell("built", "mud.vol.built")}
        {cell("dumped", "mud.vol.dumped")}
        {cell("loss_surface", "mud.vol.lossSurface", true)}
        {cell("loss_downhole", "mud.vol.lossDownhole", true)}
      </div>
    </section>
  );
}
